/**
 * Portfolio Gallery Component
 * 
 * Main gallery component that displays all portfolio items in a responsive grid.
 * This component:
 * - Imports all portfolio images from the public/images/portfolio directory
 * - Creates an array of portfolio items with image sources and alt text
 * - Renders items in a responsive CSS Grid layout (adapts from 4 columns to 1 column)
 * - Uses PortfolioItem component for each gallery item
 * 
 * The gallery showcases various products including:
 * - Religious vestments (chasubles)
 * - Personalized towels and linens with embroidered names
 * - Religious accessories and altar cloths
 * - Custom embroidery work
 * 
 * @component
 * @returns {JSX.Element} Complete portfolio gallery with all items
 */

import React from 'react';
import PortfolioItem from '../portfolio-item/portfolio-item';
import './portfolio-gallery-style.css';

const PortfolioGallery = () => {
  /**
   * Array of portfolio items with image sources and descriptions
   * Images are loaded from the public/images/portfolio directory
   * Each item includes:
   * - id: Unique identifier
   * - imageSrc: Path to the image file
   * - imageAlt: Descriptive alt text for accessibility
   */
  const portfolioItems = [
    {
      id: 1,
      imageSrc: '/images/portfolio/ChurchBags.png',
      imageAlt: 'Personalized church bags with embroidery'
    },
    {
      id: 2,
      imageSrc: '/images/portfolio/BartSimpsonEmbroidery.png',
      imageAlt: 'Towel with custom Bart Simpson embroidery'
    },
    {
      id: 3,
      imageSrc: '/images/portfolio/EladioEmbroidery.png',
      imageAlt: 'Towel embroidered with the name Eladio'
    },
    {
      id: 4,
      imageSrc: '/images/portfolio/BabyHeadbands.png',
      imageAlt: 'Personalized baby headbands with embroidery'
    },
    {
      id: 5,
      imageSrc: '/images/portfolio/BlueShirt.png',
      imageAlt: 'Blue shirt with custom embroidery'
    },
    {
      id: 6,
      imageSrc: '/images/portfolio/LightBlueGrayShirt.png',
      imageAlt: 'Light blue and gray shirt with custom embroidery'
    },
    {
      id: 7,
      imageSrc: '/images/portfolio/LibraryShirt.png',
      imageAlt: 'Library shirt with embroidery'
    },
    {
      id: 8,
      imageSrc: '/images/portfolio/DarkBlueChasuble.png',
      imageAlt: 'Dark blue priestly chasuble with embroidery'
    },
    {
      id: 9,
      imageSrc: '/images/portfolio/DetailedRedChasubles.png',
      imageAlt: 'Red chasubles with detailed religious embroidery'
    },
    {
      id: 10,
      imageSrc: '/images/portfolio/LiguistaUniform.png',
      imageAlt: 'Liguista sports uniform with embroidery'
    },
    {
      id: 11,
      imageSrc: '/images/portfolio/Aprons.png',
      imageAlt: 'Personalized aprons with embroidery'
    },
    {
      id: 12,
      imageSrc: '/images/portfolio/BlackAprons.png',
      imageAlt: 'Black aprons with custom embroidery'
    },
    {
      id: 13,
      imageSrc: '/images/portfolio/LiturgicalSet.png',
      imageAlt: 'Embroidered religious liturgical set'
    },
    {
      id: 14,
      imageSrc: '/images/portfolio/ChurchTablecloth.png',
      imageAlt: 'Church tablecloth with religious embroidery'
    },
    {
      id: 15,
      imageSrc: '/images/portfolio/EmbroideredHandkerchief.png',
      imageAlt: 'Personalized embroidered handkerchief'
    },
    {
      id: 16,
      imageSrc: '/images/portfolio/EmbroideredHandkerchiefs2.png',
      imageAlt: 'Personalized embroidered handkerchiefs'
    },
    {
      id: 17,
      imageSrc: '/images/portfolio/LoversHandkerchiefs.png',
      imageAlt: 'Couple handkerchiefs with embroidery'
    },
    {
      id: 18,
      imageSrc: '/images/portfolio/ParamedicUniform.png',
      imageAlt: 'Paramedic uniform with institutional embroidery'
    },
    {
      id: 19,
      imageSrc: '/images/portfolio/AltarBoyRobe.png',
      imageAlt: 'Custom-made altar boy robe'
    },
    {
      id: 20,
      imageSrc: '/images/portfolio/PriestVestment.png',
      imageAlt: 'Tailored and embroidered priest vestment'
    },
    {
      id: 21,
      imageSrc: '/images/portfolio/EmbroideredReligiousVestment.png',
      imageAlt: 'White religious vestment with detailed embroidery'
    }
  ];

  return (
    <div className="portfolio-gallery">
      <div className="portfolio-gallery__grid">
        {portfolioItems.map((item) => (
          <PortfolioItem
            key={item.id}
            imageSrc={item.imageSrc}
            imageAlt={item.imageAlt}
          />
        ))}
      </div>
    </div>
  );
};

export default PortfolioGallery;
